/**
 * Step 1: Environment and Usage Configuration
 */

import { useWizardState } from '@/hooks/useWizardState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PersonaList } from './PersonaList';
import { ConstraintSliders } from './ConstraintSliders';
import { UsageSliders } from './UsageSliders';

export function EnvironmentStep() {
  const { state, updateEnvironment, setPersonas, addPersona, updatePersona, removePersona } = useWizardState();
  const env = state.environment;
  const personas = state.personas;

  // Note: Persona percentages are managed within PersonaList component
  const isPersonasValid = personas.length > 0;

  return (
    <div className="space-y-8">
      {/* Section 1: User Personas */}
      <div>
        <h3 className="text-lg font-semibold mb-2">Define User Personas</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Create personas representing different types of users. Use templates for quick setup or build custom personas.
        </p>
      <PersonaList
        personas={personas}
        onAdd={addPersona}
        onUpdate={updatePersona}
        onRemove={removePersona}
        onSetPersonas={setPersonas}
      />
      </div>

      {/* Section 2: Infrastructure Constraints */}
      <div>
        <h3 className="text-lg font-semibold mb-2">Infrastructure Constraints</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Configure performance, memory, and deployment requirements.
        </p>
        <ConstraintSliders environment={env} onChange={updateEnvironment} />
      </div>

      {/* Section 3: Usage Patterns */}
      <div>
        <h3 className="text-lg font-semibold mb-2">Usage Patterns</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Estimate your user traffic and load characteristics.
        </p>
        <UsageSliders environment={env} onChange={updateEnvironment} />
      </div>

      {/* Configuration Summary */}
      <Card className="p-6 bg-primary/5 border-primary/10">
        <CardHeader className="p-0 mb-4">
          <CardTitle className="text-base">Configuration Summary</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Personas</p>
              <p className="font-semibold">{personas.length} defined</p>
            </div>
            <div>
              <p className="text-muted-foreground">Expected Concurrent</p>
              <p className="font-semibold">{env.concurrentUsers.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Latency</p>
              <p className="font-semibold capitalize">{env.latencyTolerance}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Precision</p>
              <p className="font-semibold">{env.precision}-bit</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
